import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Row, Col, Table } from 'react-bootstrap';

// Custom decoding for Safari compatibility
const dereplaceSpecialChars = (str) => {
    if (!str) return str;
    return str
        .replace(/__e__/g, 'ê')
        .replace(/__c__/g, 'ç')
        .replace(/__a1__/g, 'á')
        .replace(/__a2__/g, 'à')
        .replace(/__a3__/g, 'â')
        .replace(/__a4__/g, 'ã')
        .replace(/__e1__/g, 'é')
        .replace(/__e2__/g, 'è')
        .replace(/__o1__/g, 'ó')
        .replace(/__o2__/g, 'ò')
        .replace(/__o3__/g, 'õ')
        .replace(/__o4__/g, 'ô')
        .replace(/__i1__/g, 'í')
        .replace(/__u1__/g, 'ú')
        .replace(/__u2__/g, 'ü');
};

const formatCurrencyBRL = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '-';

    return number.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
};

const formatDate = (value) => {
    if (!value) return '-';

    const rawDate = typeof value === 'object' && value?.date ? value.date : value;
    const date = new Date(rawDate);

    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('pt-BR');
};

const normalizeKeyPart = (value) => {
    if (value === null || value === undefined) return '';

    return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();
};

const buildLicitacaoKey = (item) => {
    if (!item || typeof item !== 'object') return '';

    return [
        normalizeKeyPart(item.NUMEROPROCESSO),
        normalizeKeyPart(item.ANOPROCESSO),
        normalizeKeyPart(item.NUMEROMODALIDADE),
        normalizeKeyPart(item.ANOMODALIDADE),
        normalizeKeyPart(item.NOMEMODALIDADE),
    ].join('|');
};

// Remendo temporário no front até a API passar a devolver esses campos
const LICITACAO_DETALHES_FIXOS = {
    [buildLicitacaoKey({
        NUMEROPROCESSO: 1,
        ANOPROCESSO: 2026,
        NUMEROMODALIDADE: 1,
        ANOMODALIDADE: 2026,
        NOMEMODALIDADE: 'Concorrência - Presencial',
    })]: {
        esclarecimentos_e_respostas: [
            {
                label: 'Esclarecimentos e Respostas (Processo Suspenso)',
                href: '/licitacoes/downloads/esclarecimentos-e-respostas-concorrencia-1-2026-processo-suspenso.pdf',
            },
            {
                label: 'Esclarecimentos e Respostas para o Portal da ALEPE - Pós Retificação do Edital',
                href: '/licitacoes/downloads/ESCLARECIMENTOS E RESPOSTAS PARA O PORTAL DA ALEPE - POS RETIFICACAO DO EDITAL.pdf',
            },
        ],
    },
};

export default function LicitacaoDetalhada() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();

    const [licitacoes, setLicitacoes] = useState([]);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [apiUrl, setApiUrl] = useState('');

    useEffect(() => {
        setLoading(true);
        setError(false);

        const query = new URLSearchParams();

        if (id) {
            query.append('ID', id);
        } else {
            const numeroprocesso = dereplaceSpecialChars(searchParams.get('numeroprocesso'));
            const ano = dereplaceSpecialChars(searchParams.get('ano'));
            const modalidade = dereplaceSpecialChars(searchParams.get('modalidade'));
            const status = dereplaceSpecialChars(searchParams.get('status'));

            if (numeroprocesso) query.append('numeroprocesso', numeroprocesso);
            if (ano) query.append('ano', ano);
            if (modalidade) query.append('modalidade', modalidade);
            if (status) query.append('status', status);
        }

        const url = `https://www.alepe.pe.gov.br/licitacoes/list.php?v=2&${query
            .toString()
            .replace(/\+/g, '%20')}`;

        setApiUrl(url);

        fetch(url)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP ${res.status}`);
                }
                return res.json();
            })
            .then((json) => {
                setLicitacoes(Array.isArray(json?.data) ? json.data : []);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Erro na requisição da licitação:', err);
                setError(true);
                setLoading(false);
            });
    }, [id, searchParams]);

    const licitacao = licitacoes[0] || null;

    const detalhesFixos = useMemo(() => {
        if (!licitacao) return null;

        const licitacaoKey = buildLicitacaoKey(licitacao);
        if (!licitacaoKey) return null;

        return LICITACAO_DETALHES_FIXOS[licitacaoKey] || null;
    }, [licitacao]);

    const totalAdjudicado = useMemo(() => {
        if (!licitacoes.length) return null;

        const values = licitacoes
            .map((item) => Number(item?.TOTALADJUDICADOLICITANTE))
            .filter((value) => Number.isFinite(value));

        if (!values.length) return null;

        return values.reduce((sum, value) => sum + value, 0);
    }, [licitacoes]);

    if (loading) return <div>Carregando...</div>;

    if (error) return <div>Erro ao carregar os dados.</div>;

    if (!licitacoes.length || !licitacao) {
        return (
            <div>
                <p>Licitação não encontrada.</p>
                <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
                    <strong>Parâmetros da requisição:</strong>
                </p>
                <pre
                    style={{
                        backgroundColor: '#f5f5f5',
                        padding: '10px',
                        borderRadius: '4px',
                        overflow: 'auto',
                        fontSize: '12px',
                    }}
                >
                    {apiUrl}
                </pre>
            </div>
        );
    }

    const hasVencedores = licitacoes.some(
        (item) => item.RAZAOSOCIAL && item.RAZAOSOCIAL !== '-'
    );

    return (
        <div>
            <p>
                <small>
                    Você está em <a href="/financas">Prestação de Contas</a> /{' '}
                    <a href="/financas/licitacoes">Licitações</a> /{' '}
                    {`${licitacao.NOMEMODALIDADE} ${licitacao.NUMEROMODALIDADE}/${licitacao.ANOMODALIDADE}`}
                </small>
            </p>

            <Row>
                <Col xs="12" className="text-center">
                    <h2>{`${licitacao.NOMEMODALIDADE} ${licitacao.NUMEROMODALIDADE}/${licitacao.ANOMODALIDADE}`}</h2>
                    <h2 style={{ fontSize: '80%' }}>
                        {`Processo ${licitacao.NUMEROPROCESSO}/${licitacao.ANOPROCESSO}`}
                    </h2>
                </Col>

                <Col xs="12" className="mt-3">
                    <div className="proposicao-list-item-header">
                        <h3>Objeto conforme Edital</h3>
                    </div>
                    <div className="proposicao-list-item-text">
                        <p style={{ textAlign: 'justify' }}>
                            {licitacao.OBJETOCONFORMEEDITAL || '-'}
                        </p>
                    </div>
                </Col>

                <Col xs="12" className="mt-3">
                    <div className="proposicao-list-item-header">
                        <h3>Total Adjudicado</h3>
                    </div>
                    <div className="proposicao-list-item-text">
                        <p style={{ textAlign: 'justify' }}>
                            {totalAdjudicado !== null
                                ? formatCurrencyBRL(totalAdjudicado)
                                : 'Nenhum valor adjudicado'}
                        </p>
                    </div>
                </Col>

                {detalhesFixos?.esclarecimentos_e_respostas?.length > 0 && (
                    <Col xs="12" className="mt-3">
                        <div className="proposicao-list-item-header">
                            <h3>Esclarecimentos e Respostas</h3>
                        </div>
                        <div className="proposicao-list-item-text">
                            {detalhesFixos.esclarecimentos_e_respostas.map((item, idx) => (
                                <p key={idx} style={{ textAlign: 'justify' }}>
                                    <a
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {item.label}
                                    </a>
                                </p>
                            ))}
                        </div>
                    </Col>
                )}

                {hasVencedores && (
                    <Col xs="12" className="mt-3">
                        <h3>Vencedores</h3>
                    </Col>
                )}

                {licitacoes.map((item, idx) => (
                    <Col key={idx} xs="12" className="mt-3 mb-5 table-responsive">
                        <Table>
                            <tbody>
                                <tr>
                                    <td><strong>STATUS:</strong></td>
                                    <td>{item.SITUACAOLICITACAO || '-'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Data de Abertura:</strong></td>
                                    <td>{formatDate(item.DATASESSAOABERTURA)}</td>
                                </tr>
                                <tr>
                                    <td><strong>Vencedor:</strong></td>
                                    <td>{item.RAZAOSOCIAL || '-'}</td>
                                </tr>
                                <tr>
                                    <td><strong>CPF/CNPJ:</strong></td>
                                    <td>{item.NUMERODOCUMENTOAJUSTADO || '-'}</td>
                                </tr>
                                <tr>
                                    <td><strong>Valor Adjudicado:</strong></td>
                                    <td>{formatCurrencyBRL(item.TOTALADJUDICADOLICITANTE)}</td>
                                </tr>
                                <tr>
                                    <td><strong>Anexos:</strong></td>
                                    <td>
                                        {item.LinkArquivo ? (
                                            <a
                                                href={item.LinkArquivo.replace(
                                                    'http://sistemas.tce.pe.gov.br/audinArquivos/licon/processo_licitatorio/edital/778/',
                                                    'https://www.alepe.pe.gov.br/licitacoes/downloads/'
                                                )}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Visualizar em PDF
                                            </a>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>
                ))}
            </Row>
        </div>
    );
}